/**
 * AI-backed fallback translator — bridges the gap between our hand-curated
 * dictionary (src/i18n/dictionary.js) and the long tail of dynamic content
 * (DB tour descriptions, FAQ answers, blog post bodies, admin-edited copy
 * that wasn't anticipated in the dictionary).
 *
 * Flow:
 *   1. The DOM walker (src/i18n/domTranslator.js) finds an English text node.
 *   2. It checks the manual dictionary first — instant, high-quality.
 *   3. If no match, it queues the text for AI translation.
 *   4. We batch queued texts every ~150ms and POST to /api/translate/bulk-ui.
 *   5. Server checks D1 cache, then Lingva (Google Translate proxy) for
 *      anything not cached, persists to D1 forever.
 *   6. Client caches results in localStorage so we don't even hit the
 *      network on the next visit.
 *   7. When translations arrive, we re-apply them to the DOM.
 *
 * Why a separate file from dictionary.js: the dictionary is a synchronous
 * lookup; this module is async + network-backed + cache-heavy. Mixing them
 * would muddy the dictionary's "instant, predictable" guarantee.
 *
 * Cache layers (top to bottom = closest to user):
 *   1. localStorage  — per browser, lasts ~forever
 *   2. D1 server-side cache — per language, shared by all users
 *   3. Lingva translation — only when 1+2 miss
 */

const LOCAL_CACHE_PREFIX = 'nyc_t_';
const API_URL = '/api/translate/bulk-ui';
const BATCH_DELAY_MS = 200;
const MAX_BATCH = 40;
const MAX_TEXT_LENGTH = 1800; // server caps at 2000, leave margin

/** In-memory cache (current session) for ultra-fast repeated lookups. */
const memoryCache = new Map(); // `${lang}|${text}` → translation

/** Pending text → callbacks waiting for that text's translation. */
const pendingQueue = new Map(); // `${lang}|${text}` → Array<(t: string) => void>
let flushTimer = null;
let activeLang = 'en';

function lsGet(lang, text) {
  try {
    const k = LOCAL_CACHE_PREFIX + lang + '|' + text;
    return localStorage.getItem(k);
  } catch { return null; }
}

function lsSet(lang, text, translation) {
  try {
    const k = LOCAL_CACHE_PREFIX + lang + '|' + text;
    localStorage.setItem(k, translation);
  } catch (e) {
    // localStorage full or blocked — silently drop. The D1 server cache
    // is the authoritative store; localStorage is just a perf optimization.
  }
}

/**
 * Synchronous lookup — checks memory + localStorage caches only. Returns
 * the translation if present, null otherwise. The DOM walker uses this on
 * its initial scan so cached translations apply immediately without waiting
 * for an API round-trip.
 */
export function getCachedTranslation(text, lang) {
  if (!text || lang === 'en') return text;
  const memKey = `${lang}|${text}`;
  if (memoryCache.has(memKey)) return memoryCache.get(memKey);
  const fromLs = lsGet(lang, text);
  if (fromLs) {
    memoryCache.set(memKey, fromLs);
    return fromLs;
  }
  return null;
}

/**
 * Async translate via the server API. Returns a Promise that resolves to the
 * translated text. Auto-batches requests within a 200ms window for efficiency
 * (the DOM walker discovers dozens of strings in quick succession on each
 * page load — we want one HTTP call, not 50).
 */
export function translateAsync(text, lang) {
  if (!text || lang === 'en') return Promise.resolve(text);
  const cached = getCachedTranslation(text, lang);
  if (cached !== null) return Promise.resolve(cached);

  // Skip pathological strings to keep batches small.
  if (text.length > MAX_TEXT_LENGTH || text.length < 2) {
    return Promise.resolve(text);
  }

  activeLang = lang;
  const key = `${lang}|${text}`;
  return new Promise((resolve) => {
    if (!pendingQueue.has(key)) pendingQueue.set(key, []);
    pendingQueue.get(key).push(resolve);
    scheduleFlush();
  });
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(flushQueue, BATCH_DELAY_MS);
}

async function flushQueue() {
  flushTimer = null;
  if (pendingQueue.size === 0) return;

  // Snapshot + clear queue
  const snapshot = Array.from(pendingQueue.entries());
  pendingQueue.clear();

  // Group by language (in practice all requests share one lang, but be safe).
  const byLang = new Map();
  for (const [key, callbacks] of snapshot) {
    const [lang, ...rest] = key.split('|');
    const text = rest.join('|');
    if (!byLang.has(lang)) byLang.set(lang, new Map());
    byLang.get(lang).set(text, callbacks);
  }

  for (const [lang, items] of byLang) {
    const texts = [...items.keys()];
    // Chunk if huge.
    for (let i = 0; i < texts.length; i += MAX_BATCH) {
      const chunk = texts.slice(i, i + MAX_BATCH);
      try {
        const response = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ texts: chunk, target_lang: lang }),
        });
        if (!response.ok) throw new Error('Translate API ' + response.status);
        const json = await response.json();
        const out = json.translations || {};
        for (const text of chunk) {
          const translated = out[text] || text;
          memoryCache.set(`${lang}|${text}`, translated);
          if (translated !== text) lsSet(lang, text, translated);
          const callbacks = items.get(text) || [];
          for (const cb of callbacks) cb(translated);
        }
      } catch (err) {
        // Network/server failure — resolve everyone with their original
        // English text so the UI doesn't hang.
        // eslint-disable-next-line no-console
        console.warn('[i18n] batch translate failed:', err.message);
        for (const text of chunk) {
          const callbacks = items.get(text) || [];
          for (const cb of callbacks) cb(text);
        }
      }
    }
  }
}

/** Reset memory cache when language switches (localStorage stays — it's
 *  per-language keyed and we want to reuse it next time the user picks
 *  this language again). */
export function setActiveLanguage(lang) {
  activeLang = lang;
  // Memory cache is keyed by lang|text so it stays valid across switches.
  // But we DO want to clear the pending queue so old-language requests
  // don't fire under the new language.
  pendingQueue.clear();
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
}

/** Eagerly preload translations for a list of strings — used by the DOM
 *  walker to warm the cache for everything visible on the page in one
 *  batch (faster than waiting for individual lookups). */
export async function preloadTranslations(texts, lang) {
  if (!texts || !texts.length || lang === 'en') return;
  const uncached = texts.filter((t) => getCachedTranslation(t, lang) === null);
  if (!uncached.length) return;
  // Fire them all through translateAsync — they'll auto-batch.
  await Promise.all(uncached.map((t) => translateAsync(t, lang)));
}
